import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { api } from '../lib/api';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactMutation = useMutation({
    mutationFn: (data: { name: string; email: string; subject: string; message: string }) =>
      api.post('/contact', data),
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to send message');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (message.length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }
    contactMutation.mutate({ name, email, subject, message });
  };

  return (
    <MarketingLayout>
      <section className="section">
        <div className="container">
          <div className="demo-grid">
            {/* Left — intro + other ways to reach us */}
            <div className="reveal">
              <span className="kicker">§ CONTACT</span>
              <h1 className="h2 balance" style={{ marginTop: '1.25rem', maxWidth: '14ch' }}>
                Have a question? We&rsquo;re here to help.
              </h1>
              <p className="lead" style={{ marginTop: '1.25rem', maxWidth: '44ch' }}>
                Tell us what you need and we&rsquo;ll get back to you &mdash; typically within one
                business day.
              </p>
              <ul className="demo-points">
                <li><CheckIcon aria-hidden="true" /><span>Email us directly at <a href="mailto:support@clarid.ai" style={{ color: 'var(--teal-hover)', textDecoration: 'underline' }}>support@clarid.ai</a></span></li>
                <li><CheckIcon aria-hidden="true" /><span>Looking for a walkthrough? <a href="/request-demo" style={{ color: 'var(--teal-hover)', textDecoration: 'underline' }}>Request a demo</a> instead.</span></li>
                <li><CheckIcon aria-hidden="true" /><span>We respond within 24 hours on business days.</span></li>
              </ul>
              <p className="meta" style={{ marginTop: '2rem' }}>
                <a href="/privacy" style={{ color: 'inherit' }}>Privacy</a> &middot;{' '}
                <a href="/terms" style={{ color: 'inherit' }}>Terms</a>
              </p>
            </div>

            {/* Right — form / success */}
            <div className="reveal">
              {isSubmitted ? (
                <div className="form-card" style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: '3.25rem', height: '3.25rem', margin: '0 auto 1.25rem',
                      borderRadius: '999px', background: 'var(--teal-pill)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <CheckIcon aria-hidden="true" style={{ width: '1.6rem', height: '1.6rem', color: 'var(--teal-hover)' }} />
                  </div>
                  <h2 className="h3">Message sent.</h2>
                  <p className="body" style={{ marginTop: '0.6rem' }}>
                    Thanks for reaching out &mdash; we&rsquo;ll get back to you within one business day.
                  </p>
                </div>
              ) : (
                <form className="form-card" onSubmit={handleSubmit} noValidate>
                  <div className="field">
                    <label htmlFor="name">Name</label>
                    <input id="name" type="text" required value={name}
                      onChange={(e) => setName(e.target.value)} placeholder="John Doe" />
                  </div>
                  <div className="field">
                    <label htmlFor="email">Email</label>
                    <input id="email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="you@yourbank.com" />
                  </div>
                  <div className="field">
                    <label htmlFor="subject">Subject</label>
                    <input id="subject" type="text" required value={subject}
                      onChange={(e) => setSubject(e.target.value)} placeholder="How can we help?" />
                  </div>
                  <div className="field">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" rows={6} required value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us more about your question or issue..." />
                    <p className="meta" style={{ marginTop: '0.4rem' }}>Minimum 10 characters</p>
                  </div>
                  <button type="submit" className="btn btn-primary btn-block" disabled={contactMutation.isPending}>
                    {contactMutation.isPending ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
